export interface DocumentVersion {
  versionUuid: string;
  versionNumber: number;
  url: string;
  contentType?: string;
  fileSize?: number;
  createdDt: string;
  metadata?: Record<string, unknown> | null;
  /** Derived from version metadata when present */
  displayName?: string | null;
  /** `salon_document_versions.created_by_staff_id` → `salon_staff.id` */
  createdByStaffId?: string | null;
  /** Display name for the staff member who uploaded this version */
  createdByStaffName?: string | null;
}

export interface SalonDocument {
  documentKey: string;
  documentUuid: string;
  clientId?: string | null;
  currentVersionUuid?: string | null;
  currentVersionNumber: number;
  currentUrl: string;
  currentContentType?: string;
  currentFileSize?: number;
  currentCreatedDt: string;
  /** From current version metadata when present */
  displayName?: string | null;
  currentMetadata?: Record<string, unknown> | null;
  /**
   * When the API returns who uploaded the current version (not always present).
   * May also appear in `currentMetadata` under keys like `uploadedByName`.
   */
  uploadedByName?: string | null;
  /** Last save attribution (`salon_staff.id`) — list endpoint */
  lastSavedByStaffId?: string | null;
  /** Display name for who last saved the document — list endpoint */
  lastSavedByStaffName?: string | null;
}

export interface DocumentListResponse {
  documents: SalonDocument[];
}

/**
 * Human-readable label for a salon document: the version metadata `displayName`
 * when present, otherwise the (slug) document key, otherwise a generic fallback.
 * Shared so the attachment picker and the saved-attachment label resolve names
 * identically.
 */
export const documentDisplayName = (d: SalonDocument): string =>
  d.displayName?.trim() || d.documentKey || "Untitled document";

export interface DocumentDetailResponse {
  documentKey: string;
  documentUuid: string;
  clientId?: string | null;
  currentVersionId?: number;
  displayName?: string | null;
  versions: DocumentVersion[];
}

export interface DocumentUploadResponse {
  message: string;
  documentKey: string;
  versionNumber: number;
  versionUuid: string;
  url: string;
  s3Key: string;
  clientId?: string | null;
}

export interface DocumentRevertResponse {
  message: string;
  currentVersionUuid: string;
}

/** `DELETE /business-locations/{salonId}/documents/{documentKey}` success body */
export interface DocumentDeleteResponse {
  message: string;
  documentKey: string;
  removedVersionCount: number;
}

/**
 * Success payload from authenticated PHI `GET .../documents/{key}/download?clientId=`.
 * Raw bytes + optional filename from `Content-Disposition`.
 *
 * The endpoint uses `BinaryMediaTypes: application/octet-stream` so the wire
 * `Content-Type` is always `application/octet-stream`. The real MIME type
 * (e.g. `image/png`) is in the `X-Content-Type` response header.
 */
export interface SalonDocumentPhidDownload {
  blob: Blob;
  /** Parsed from `Content-Disposition: attachment; filename="..."` when present */
  serverFilename?: string;
  /** Real file MIME from `X-Content-Type` (falls back to `Content-Type`) */
  responseContentType?: string;
}
